import type { CatalogProduct, CatalogSchedule } from "@school-sales-ai/shared";
import type { CatalogEditor, CatalogList } from "./useCatalogEditor";

/**
 * The small glue every catalog editor uses to wire an input to its row: save on
 * blur and Enter, and read the row's visible validation messages.
 */

/** Blur and Enter both save the field now, cancelling the debounce. */
export function commitHandlers(
  editor: CatalogEditor,
  list: CatalogList,
  key: string,
): {
  onBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
} {
  return {
    onBlur: () => void editor.commit(list, key),
    onKeyDown: (e) => {
      if (e.key === "Enter") void editor.commit(list, key);
    },
  };
}

/** Every visible validation message on a row, in field order. */
export function rowErrorMessages(
  editor: CatalogEditor,
  list: CatalogList,
  key: string,
): string[] {
  return [...editor.fieldErrors(list, key).values()].flat();
}

/** True when the field has a visible validation message — paints the input red. */
export function fieldInvalid(
  editor: CatalogEditor,
  list: CatalogList,
  key: string,
  field: string,
): boolean {
  return (editor.fieldErrors(list, key).get(field)?.length ?? 0) > 0;
}

/** A program offered in every open cycle. */
export const CYCLES_SCHEDULE: CatalogSchedule = { type: "cycles", startDate: null, endDate: null };

/** A program with its own dates, none confirmed yet — valid as it stands. */
export const UNDATED_SCHEDULE: CatalogSchedule = {
  type: "specific_dates",
  startDate: null,
  endDate: null,
};

/** A product's schedule once it switches to the other kind. */
export function switchedSchedule(product: CatalogProduct): CatalogSchedule {
  return product.schedule?.type === "specific_dates" ? CYCLES_SCHEDULE : UNDATED_SCHEDULE;
}

/**
 * What a `cycles`-type program is called. With cycles it joins the next open
 * one; without them (open enrollment — an academy) the same row means the
 * prospect can start any time, and the agent already says so. Only the WORD
 * changes with the switch, never the data.
 */
export function cyclesTypeLabel(usesCycles: boolean): string {
  return usesCycles ? "Por ciclo" : "Inscripción abierta";
}
